import { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF, PolylineF } from "@react-google-maps/api";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { BatteryMedium, BatteryLow, WarningCircle, ArrowsClockwise, Play, Pause, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const POINT_COLORS = { login: "#007AFF", visit: "#0A0A0A", logout: "#FF3B30", live: "#FFCC00" };
const DEFAULT_CENTER = { lat: 20.59, lng: 78.96 };
const MAP_STYLE = { width: "100%", height: "100%" };
const isOnline = (ts) => ts && Date.now() - new Date(ts).getTime() < 15 * 60 * 1000;
const LOW_BATTERY = 20;
const OFFLINE_MS = 60 * 60 * 1000;
const isLowBattery = (u) => u.last_location?.battery_level != null && u.last_location.battery_level <= LOW_BATTERY;
const isOfflineAtWork = (u) =>
  u.attendance && !u.attendance.logout_time && u.last_location?.ts &&
  Date.now() - new Date(u.last_location.ts).getTime() > OFFLINE_MS;
const agoSafe = (ts, opts = { addSuffix: true }) => {
  if (!ts) return "—";
  const d = parseISO(ts);
  return isNaN(d) ? "—" : formatDistanceToNow(d, opts);
};

const fmtTs = (ts) =>
  ts ? new Date(ts).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour12: false }) : "—";
const lerp = (a, b, t) => ({ lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t });

function usePlayback(coords) {
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const raf = useRef();

  useEffect(() => {
    if (!playing || coords.length < 2) return;
    let last = performance.now();
    const totalMs = 25000;
    const step = (now) => {
      const dt = ((now - last) * speed) / totalMs;
      last = now;
      setProgress((p) => Math.min(p + (Number.isFinite(dt) ? dt : 0), 1));
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [playing, speed, coords.length]);

  useEffect(() => {
    if (progress >= 1 && playing) setPlaying(false);
  }, [progress, playing]);

  const position = useMemo(() => {
    if (coords.length === 0) return null;
    if (coords.length === 1) return coords[0];
    const p = Math.min(Math.max(progress, 0), 1);
    const seg = p * (coords.length - 1);
    const i = Math.min(Math.floor(seg), coords.length - 2);
    const a = coords[i];
    const b = coords[i + 1];
    if (!a || !b) return coords[coords.length - 1] || null;
    return lerp(a, b, seg - i);
  }, [coords, progress]);

  const currentIndex = coords.length > 1
    ? Math.min(Math.round(Math.min(Math.max(progress, 0), 1) * (coords.length - 1)), coords.length - 1)
    : 0;
  return { progress, setProgress, playing, setPlaying, speed, setSpeed, position, currentIndex };
}

const dotIcon = (color, scale = 8) => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  scale,
  fillColor: color,
  fillOpacity: 1,
  strokeColor: "#ffffff",
  strokeWeight: 2.5,
});

export default function LiveMap() {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY });
  const [mode, setMode] = useState("live");
  const [team, setTeam] = useState([]);
  const [updated, setUpdated] = useState(null);
  const [selected, setSelected] = useState(null);
  const [routeEmp, setRouteEmp] = useState("");
  const [routeDate, setRouteDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [route, setRoute] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [map, setMap] = useState(null);
  const [liveKm, setLiveKm] = useState({});

  const load = () =>
    api.get("/proxy/users").then((r) => {
      setTeam(r.data.filter((u) => u.role === "employee"));
      setUpdated(new Date());
    }).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const loadKm = () => api.get("/live-km").then((r) => setLiveKm(r.data)).catch(() => {});
    loadKm();
    const t = setInterval(loadKm, 120000);
    return () => clearInterval(t);
  }, []);

  const located = team.filter((u) => u.last_location);
  const lowBattery = located.filter(isLowBattery);
  const offlineAtWork = located.filter(isOfflineAtWork);
  const routeCoords = useMemo(() => (route?.points || []).map((p) => ({ lat: p.lat, lng: p.lng })), [route]);
  const trail = useMemo(() => route?.trail || [], [route]);
  const playCoords = useMemo(
    () => (trail.length >= 2 ? trail.map((p) => ({ lat: p.lat, lng: p.lng })) : routeCoords),
    [trail, routeCoords]
  );
  const pb = usePlayback(playCoords);

  const currentTs = trail.length >= 2 ? trail[Math.min(pb.currentIndex, trail.length - 1)]?.ts : null;
  const activeStopIndex = useMemo(() => {
    if (!route?.points?.length) return -1;
    if (!currentTs) return pb.currentIndex;
    let idx = -1;
    route.points.forEach((p, i) => { if ((p.ts || "") <= currentTs) idx = i; });
    return idx;
  }, [route, currentTs, pb.currentIndex]);

  const activeCoords = mode === "live"
    ? located.map((u) => ({ lat: u.last_location.latitude, lng: u.last_location.longitude }))
    : playCoords;

  useEffect(() => {
    if (!map || !isLoaded || activeCoords.length === 0) return;
    if (activeCoords.length === 1) {
      map.setCenter(activeCoords[0]);
      map.setZoom(14);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    activeCoords.forEach((c) => bounds.extend(c));
    map.fitBounds(bounds, 60);
  }, [mode, route, team.length, isLoaded, map]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRoute = async () => {
    if (!routeEmp) { toast.error("Select an employee first"); return; }
    setLoadingRoute(true);
    try {
      const r = await api.get(`/track/${routeEmp}`, { params: { date: routeDate } });
      setRoute(r.data);
      pb.setProgress(0);
      pb.setPlaying(false);
      if (r.data.points.length === 0 && (r.data.trail || []).length === 0) toast.info("No recorded activity for that day");
    } catch {
      toast.error("Failed to load route");
    } finally {
      setLoadingRoute(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100dvh-7.5rem)] lg:h-screen" data-testid="live-map-page">
      <div className="relative h-[42%] shrink-0 lg:h-auto lg:flex-1">
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={MAP_STYLE}
            center={DEFAULT_CENTER}
            zoom={5}
            onLoad={(m) => setMap(m)}
            options={{ streetViewControl: false, mapTypeControl: true, fullscreenControl: false, clickableIcons: false }}
          >
            {mode === "live" && located.map((u) => (
              <MarkerF
                key={u.id}
                position={{ lat: u.last_location.latitude, lng: u.last_location.longitude }}
                icon={dotIcon(isOfflineAtWork(u) ? "#FF3B30" : isLowBattery(u) ? "#FF9500" : isOnline(u.last_location.ts) ? "#007AFF" : "#94a3b8", isOfflineAtWork(u) || isLowBattery(u) ? 10 : 8)}
                onClick={() => setSelected(u.id)}
              />
            ))}
            {mode === "live" && selected && (() => {
              const u = located.find((x) => x.id === selected);
              if (!u) return null;
              return (
                <InfoWindowF
                  position={{ lat: u.last_location.latitude, lng: u.last_location.longitude }}
                  onCloseClick={() => setSelected(null)}
                  options={{ pixelOffset: new window.google.maps.Size(0, -12) }}
                >
                  <div style={{ fontFamily: "IBM Plex Sans, sans-serif", minWidth: 160 }}>
                    <p style={{ fontWeight: 700, margin: 0 }}>{u.name}</p>
                    <p style={{ margin: "2px 0", fontSize: 12, color: "#475569" }}>{u.employee_id} · {u.designation}</p>
                    <p style={{ margin: "2px 0", fontSize: 12, color: isLowBattery(u) ? "#FF3B30" : "inherit", fontWeight: isLowBattery(u) ? 700 : 400 }}>
                      Battery: {u.last_location.battery_level ?? "?"}%{isLowBattery(u) ? " · LOW" : ""}
                    </p>
                    <p style={{ margin: "2px 0", fontSize: 12 }}>Speed: {u.last_location.speed ?? 0} km/h</p>
                    <p style={{ margin: "2px 0", fontSize: 12, color: "#475569" }}>
                      Seen {agoSafe(u.last_location.ts)}
                    </p>
                  </div>
                </InfoWindowF>
              );
            })()}
            {mode === "route" && route && (
              <>
                <PolylineF path={playCoords} options={{ strokeColor: "#007AFF", strokeWeight: 3, strokeOpacity: 0.8 }} />
                {route.points.map((p, i) => (
                  <MarkerF
                    key={i}
                    position={{ lat: p.lat, lng: p.lng }}
                    icon={dotIcon(POINT_COLORS[p.type] || "#0A0A0A", 10)}
                    label={{ text: String(i + 1), color: "#ffffff", fontSize: "10px", fontWeight: "700" }}
                    title={`${p.label} · ${fmtTs(p.ts)}`}
                    zIndex={10 + i}
                  />
                ))}
                {pb.position && (
                  <MarkerF position={pb.position} icon={dotIcon("#FF3B30", 9)} zIndex={1000} />
                )}
              </>
            )}
          </GoogleMap>
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-slate-400" data-testid="map-loading">Loading Google Maps…</div>
        )}
      </div>

      <aside className="w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-slate-200 flex flex-col bg-white z-[10] flex-1 lg:flex-none min-h-0">
        <div className="flex border-b border-slate-200">
          <button data-testid="map-tab-live" onClick={() => setMode("live")} className={`flex-1 py-3 text-sm font-semibold transition-colors duration-150 ${mode === "live" ? "bg-slate-950 text-white" : "hover:bg-slate-50"}`}>
            Live
          </button>
          <button data-testid="map-tab-route" onClick={() => setMode("route")} className={`flex-1 py-3 text-sm font-semibold border-l border-slate-200 transition-colors duration-150 ${mode === "route" ? "bg-slate-950 text-white" : "hover:bg-slate-50"}`}>
            Route Playback
          </button>
        </div>

        {mode === "live" ? (
          <>
            <div className="px-4 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Live Tracking</p>
                <p className="font-heading font-bold text-lg tracking-tight">{located.length} on map</p>
              </div>
              <button onClick={load} data-testid="map-refresh-btn" className="p-2 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors duration-150">
                <ArrowsClockwise size={16} />
              </button>
            </div>
            {offlineAtWork.length > 0 && (
              <div className="px-4 py-2.5 bg-red-50 border-b border-red-200 flex items-center gap-2" data-testid="offline-alert-banner">
                <WarningCircle size={15} weight="fill" className="text-[#FF3B30] shrink-0" />
                <p className="text-xs text-red-800 font-semibold">
                  No GPS for over 1h while on duty: {offlineAtWork.map((u) => `${u.name.trim()} (${agoSafe(u.last_location.ts, {})})`).join(", ")}
                </p>
              </div>
            )}
            {lowBattery.length > 0 && (
              <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2" data-testid="low-battery-banner">
                <WarningCircle size={15} weight="fill" className="text-[#FF9500] shrink-0" />
                <p className="text-xs text-amber-800 font-semibold">
                  Low battery (≤{LOW_BATTERY}%): {lowBattery.map((u) => `${u.name.trim()} (${u.last_location.battery_level}%)`).join(", ")} — tracking may go dark
                </p>
              </div>
            )}
            <div className="flex-1 overflow-y-auto" data-testid="map-employee-list">
              {located.map((u) => {
                const online = isOnline(u.last_location.ts);
                const low = isLowBattery(u);
                const offline = isOfflineAtWork(u);
                const km = liveKm[u.id] ?? u.attendance?.distance_km;
                return (
                  <button key={u.id} onClick={() => setSelected(u.id)} className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${offline ? "bg-red-50/70" : low ? "bg-amber-50/60" : ""}`} data-testid={`map-employee-${u.employee_id}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${offline ? "bg-[#FF3B30]" : low ? "bg-[#FF9500]" : online ? "bg-[#007AFF]" : "bg-slate-300"}`} />
                      <p className="text-sm font-semibold flex-1">{u.name}</p>
                      {offline && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-white bg-[#FF3B30] px-1.5 py-0.5 rounded-sm" data-testid={`offline-${u.employee_id}`}>
                          Offline
                        </span>
                      )}
                      <span className={`flex items-center gap-1 text-xs font-mono-data ${low ? "text-[#FF3B30] font-bold" : "text-slate-500"}`} data-testid={low ? `low-battery-${u.employee_id}` : undefined}>
                        {low ? <BatteryLow size={14} weight="fill" /> : <BatteryMedium size={14} />} {u.last_location.battery_level ?? "?"}%
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-4">
                      {agoSafe(u.last_location.ts)} · {u.last_location.speed ?? 0} km/h
                      {km != null && (
                        <span className="text-slate-950 font-semibold"> · {km} km today</span>
                      )}
                    </p>
                  </button>
                );
              })}
              {located.length === 0 && <p className="p-4 text-sm text-slate-400">No location data reported yet.</p>}
            </div>
            {updated && (
              <p className="px-4 py-2 text-[11px] text-slate-400 border-t border-slate-200">
                Auto-refreshes every 30s · Updated {updated.toLocaleTimeString()}
              </p>
            )}
          </>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden" data-testid="live-tracking-panel">
            <div className="px-4 py-4 border-b border-slate-200 space-y-3">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Replay a day's trail</p>
              <select data-testid="route-employee-select" value={routeEmp} onChange={(e) => setRouteEmp(e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007AFF]">
                <option value="">Select employee…</option>
                {team.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.employee_id})</option>)}
              </select>
              <input type="date" data-testid="route-date-input" value={routeDate} onChange={(e) => setRouteDate(e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-[#007AFF]" />
              <button data-testid="load-route-btn" onClick={loadRoute} disabled={loadingRoute} className="w-full bg-[#007AFF] text-white rounded-md py-2 text-sm font-semibold hover:bg-[#0066d6] transition-colors duration-150 disabled:opacity-60">
                {loadingRoute ? "Loading…" : "Load Route"}
              </button>
            </div>

            {route && (route.points.length > 0 || trail.length >= 2) && (
              <>
                <div className="px-4 py-3 border-b border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <button data-testid="playback-toggle-btn" onClick={() => { if (pb.progress >= 1) pb.setProgress(0); pb.setPlaying(!pb.playing); }} className="p-2 bg-slate-950 text-white rounded-md hover:bg-slate-800 transition-colors duration-150">
                      {pb.playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
                    </button>
                    <button data-testid="playback-reset-btn" onClick={() => { pb.setProgress(0); pb.setPlaying(false); }} className="p-2 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors duration-150">
                      <ArrowCounterClockwise size={14} />
                    </button>
                    <input
                      type="range" min="0" max="1000" data-testid="playback-slider"
                      value={pb.progress * 1000}
                      onChange={(e) => { pb.setPlaying(false); pb.setProgress(e.target.value / 1000); }}
                      className="flex-1 accent-[#007AFF]"
                    />
                    <select data-testid="playback-speed-select" value={pb.speed} onChange={(e) => pb.setSpeed(Number(e.target.value))} className="border border-slate-200 rounded-md px-1.5 py-1 text-xs font-mono-data">
                      <option value={0.5}>0.5x</option>
                      <option value={1}>1x</option>
                      <option value={2}>2x</option>
                      <option value={4}>4x</option>
                    </select>
                  </div>
                  <p className="text-xs text-slate-500 font-mono-data">
                    {route.employee.name} · {route.date} · {route.points.length} stops
                    {trail.length >= 2 && ` · ${trail.length} GPS points`}
                    {(route.trail_km ?? route.attendance?.distance_km) != null && ` · ${route.trail_km ?? route.attendance.distance_km} km travelled`}
                  </p>
                </div>
                <div className="flex-1 overflow-y-auto" data-testid="route-timeline">
                  {route.points.map((p, i) => (
                    <div key={i} className={`px-4 py-2.5 border-b border-slate-100 flex items-center gap-3 ${i === activeStopIndex ? "bg-blue-50" : ""}`}>
                      <span className="w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center shrink-0" style={{ background: POINT_COLORS[p.type] || "#0A0A0A" }}>
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{p.label}</p>
                        <p className="text-xs text-slate-500 font-mono-data">{fmtTs(p.ts)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {route && route.points.length === 0 && trail.length < 2 && (
              <p className="p-4 text-sm text-slate-400" data-testid="route-empty">No recorded activity for {route.employee.name} on {route.date}.</p>
            )}
            <p className="px-4 py-2 text-[11px] text-slate-400 border-t border-slate-200">
              Trail is built from recorded GPS points: day login, shop visits, day logout and last position.
            </p>
          </div>
        )}
      </aside>
    </div>
  );
}
